// @flow

import './MobileHeader.scss';

import { BOIWhiteLogo } from '@boi/core/lib';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faSignInAlt,
  faSignOutAlt,
  faUser,
  faUserCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, withRouter } from 'react-router-dom';

import { CognitoContext } from '../../CognitoUtils';
import { darkBlue, greyDark } from '../../constants/colors';
import { allInsuranceTypes } from '../../constants/insuranceTypeConstants';
import { isAgent } from '../../helpers';
import { edgeAriaLabel } from '../../helpers';
import { getAgentName } from '../../helpers/AgentCheck';
import { journeyEnabled } from '../../helpers/FeatureHelper';
import { routes } from '../../routes';
import type { InsuranceType, NavigationItem } from '../../types/commonTypes';
import { goToUrlForcingLoggedIn } from '../../utils/utils';
import Navigation from '../Navigation';
import PopOverWrapper from '../PopOverWrapper/PopOverWrapper';

type UserMenuItem = NavigationItem & {
  icon: IconDefinition
};

const MobileHeader = () => {
  const className = 'c-MobileHeader';
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userPopOverVisible, setUserPopOverVisible] = useState(false);

  const { cognitoUser, feature } = useContext(CognitoContext);
  const history = useHistory();

  const userMenuItems = ((): UserMenuItem[] => {
    if (isLoggedIn) {
      return [
        {
          displayText: 'Logout',
          link: routes.logoutPage.url,
          icon: faSignOutAlt
        }
      ];
    }
    if (isAgent()) {
      return [];
    }
    return [
      { displayText: 'Login', link: routes.loginPage.url, icon: faSignInAlt },
      {
        displayText: 'Sign Up',
        link: routes.signUp.url,
        icon: faUser
      }
    ];
  })();

  const goToUrlLoggedIn = (url: string) => goToUrlForcingLoggedIn(url, history);

  const getNavigationItems = (): Array<Array<NavigationItem>> => [
    [
      { displayText: 'Home', link: routes.wallet.url },
      {
        displayText: 'New Quote',
        listItems: allInsuranceTypes
          .filter(journeyEnabled(feature.flags))
          .map(newQuoteDropdownItem)
      },
      {
        displayText: 'Manage My Insurance',
        action: () => goToUrlLoggedIn(routes.manageMyInsuranceDashboard.url)
      },
      {
        displayText: 'Renew',
        action: () => goToUrlLoggedIn(`${routes.viewMultiplePolicies.url}home`)
      }
    ],
    // $FlowFixMe
    userMenuItems
  ];

  const newQuoteDropdownItem = (
    insuranceType: InsuranceType
  ): NavigationItem => {
    switch (insuranceType) {
      case 'car':
        return {
          displayText: 'Car',
          link: `${routes.getGenericQuotePage.url}car`
        };
      case 'home':
        return {
          displayText: 'Home',
          link: `${routes.getQuotePage.url}aboutYou`
        };
      case 'travel':
        return {
          displayText: 'Travel',
          link: `${routes.getGenericQuotePage.url}travel`
        };
      default:
        return (insuranceType: empty);
    }
  };

  const reRoute = (url: ?string) => (url ? () => history.push(url) : undefined);

  const renderUserMenuItem = (item: UserMenuItem, index: number) => (
    <div
      className={`${className}__menuItemGrid`}
      onClick={reRoute(item.link)}
      tabIndex={0}
      key={`userMenuItem_${index}`}
    >
      <div>
        <span>{item.displayText}</span>
      </div>
      <div>
        <FontAwesomeIcon icon={item.icon} size="1x" color={greyDark} />
      </div>
    </div>
  );

  const titleHeader = () => {
    const expandOnTab = (e: SyntheticKeyboardEvent<HTMLInputElement>) => {
      if (['Enter', 'ArrowDown'].includes(e.key)) {
        setUserPopOverVisible(true);
      }
    };

    const profileLabel = `profile icon`;

    return (
      <span tabIndex={0} onKeyDown={expandOnTab} aria-label={profileLabel}>
        {edgeAriaLabel(profileLabel)}
        {isLoggedIn && (
          <span className={`${className}__navBarUserCircleIcon`}>
            {cognitoUser?.attributes?.given_name || getAgentName(cognitoUser)}
          </span>
        )}
        <FontAwesomeIcon
          className={`${className}__navBarIcon`}
          icon={faUserCircle}
          size="1x"
          color={greyDark}
          alt="User icon"
        />
      </span>
    );
  };

  useEffect(() => {
    setIsLoggedIn(!!cognitoUser);
  }, [cognitoUser]);

  return (
    <div className={`${className}`}>
      <div
        className={`${className}__menuItems ${className}${
          isLoggedIn ? '__loggedInMenuItem' : '__nonLoggedInMenuItem'
        }`}
      >
        <div className={`${className}__popOverMenuItems`}>
          <PopOverWrapper
            clickTitle={titleHeader()}
            visibility={userPopOverVisible}
            updateVisibility={setUserPopOverVisible}
          >
            {userMenuItems.map(renderUserMenuItem)}
          </PopOverWrapper>
        </div>
      </div>
      <div
        className={`${className}__logo`}
        onClick={() => history.push(routes.wallet.url)}
      >
        <BOIWhiteLogo color={darkBlue} />
      </div>
      <div className={`${className}__barIcon`}>
        <Navigation menuItems={getNavigationItems()} />
      </div>
    </div>
  );
};

export default withRouter(MobileHeader);
